import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import api from "../../../../services/api";
import { BackButton } from "../../../../components/button/back-button";
import { Table } from "../../../../components/table";
import { ConfirmationModal } from "../../../../components/confirmation-modal";
import { useToast } from "../../../../contexts/toast/toast-context";
import { formatPhoneWithCountryCode, getApiErrorMessage } from "../../../../utils";
import type { BlockedContact } from "../../../../types/blocked-contact";

type BlockedContactListResponse = {
  data: BlockedContact[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

export default function BlockedContacts() {
  const { success, error } = useToast();

  const [blockedContacts, setBlockedContacts] = useState<BlockedContact[]>([]);
  const [contactToDelete, setContactToDelete] = useState<BlockedContact | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10);

  useEffect(() => {
    fetchBlockedContacts(1);
  }, []);

  async function fetchBlockedContacts(pageNumber = 1) {
    try {
      const response = await api.get<BlockedContactListResponse>(
        "/blocked-contact",
        {
          params: { page: pageNumber, limit },
        },
      );

      setBlockedContacts(response.data.data);
      setPage(response.data.page);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    }
  }

  async function deleteBlockedContact(id: string) {
    if (isDeleting) return;
    try {
      setIsDeleting(true);
      await api.delete(`/blocked-contact/${id}`);
      setBlockedContacts((prev) => prev.filter((c) => c.id !== id));
      setContactToDelete(null);
      success("Contato removido da lista de bloqueios.");
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    } finally {
      setIsDeleting(false);
    }
  }

  const columns = [
    {
      key: "userName",
      label: "Nome",
      render: (contact: BlockedContact) => contact.userName || "-",
    },
    {
      key: "dialCode",
      label: "Telefone",
      render: (contact: BlockedContact) =>
        contact.countryCode && contact.dialCode
          ? formatPhoneWithCountryCode(contact.dialCode, contact.countryCode)
          : "-",
    },
    { key: "createdAt", label: "Criado em" },
    {
      key: "actions",
      label: "Ações",
      render: (contact: BlockedContact) => (
        <button
          onClick={() => setContactToDelete(contact)}
          className="btn-link-danger flex w-full items-center justify-center"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      ),
    },
  ];

  return (
    <div className="app-page">
      <BackButton />
      <div className="flex items-center justify-between mb-6">
        <h1 className="app-page-title">Contatos bloqueados</h1>
      </div>

      <Table<BlockedContact>
        columns={columns}
        data={blockedContacts}
        page={page}
        totalPages={totalPages}
        onPageChange={(newPage) => fetchBlockedContacts(newPage)}
        emptyMessage="Nenhum contato bloqueado."
      />

      <ConfirmationModal
        isOpen={!!contactToDelete}
        title="Remover bloqueio"
        description="Deseja remover este número da lista de bloqueados?"
        onCancel={() => setContactToDelete(null)}
        onConfirm={() =>
          contactToDelete?.id && deleteBlockedContact(contactToDelete.id)
        }
        isLoading={isDeleting}
      />
    </div>
  );
}
